import { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter } from 'react-router-dom';

import { CheckoutContextProvider } from '@zooplus/checkout-state';
import { SiteProvider } from '@zooplus/commons-lang';
import { SnackbarQueueProvider } from '@zooplus/ui-components';

import config from '../config';

import { CheckoutSkeleton } from '@components/common/checkout-skeleton';
import LazyLoader from '@components/common/lazy-loader';
import MainLayoutContainer from '@components/common/main-layout-container';
import MetaTag from '@components/common/meta';
import PageThemeContainer from '@components/common/page-theme-container';
import SentryErrorBoundary from '@components/common/sentry-error-boundary';
import VersionChecker from '@components/common/version-checker';
import { AppConfigContextProps } from '@config/get-app-config';
import { useMobileApp } from '@hooks/use-mobile-app';
import { initializeI18n } from '@services/i18n-service';
import { logError } from '@services/sentry-service';
import { ADOBE_DATA_LAYER } from '@utilities/constants';

import AppRoutes from './routes';
import commonStyles from './styles/common.module.scss';

// CSS Variables
import './styles/variables.scss';

const Header = lazy(() => LazyLoader(() => import('@components/common/header')));
const Footer = lazy(() => LazyLoader(() => import('@components/common/footer')));
const I18nextProvider = lazy(() =>
  LazyLoader(() =>
    import('react-i18next').then(({ I18nextProvider }) => ({ default: I18nextProvider })),
  ),
);

const App = ({ site }: AppConfigContextProps) => {
  const isMobileAppEnabled = useMobileApp();
  const initializeAdobeDataLayer = function () {
    if (!window?.[ADOBE_DATA_LAYER]) {
      window[ADOBE_DATA_LAYER] = [];
    }
  };

  useEffect(() => {
    initializeAdobeDataLayer();
  }, []);

  return (
    <>
      <SentryErrorBoundary>
        <Suspense fallback={<CheckoutSkeleton isFullPage={true} />}>
          <SiteProvider site={site} locale={site.locale}>
            <I18nextProvider i18n={initializeI18n(site.locale)}>
              <div
                data-testid="checkout-frontend"
                data-realm={site.realm}
                className={commonStyles.flexPageContainer}>
                <CheckoutContextProvider onError={(err) => logError(err)}>
                  <SnackbarQueueProvider closeButtonAriaLabel="Close">
                    <MetaTag />
                    {!isMobileAppEnabled && <Header />}
                    <BrowserRouter basename={config.baseRoutePath}>
                      <PageThemeContainer>
                        <MainLayoutContainer>
                          <VersionChecker />
                          <AppRoutes />
                        </MainLayoutContainer>
                      </PageThemeContainer>
                    </BrowserRouter>
                    {!isMobileAppEnabled && <Footer />}
                  </SnackbarQueueProvider>
                </CheckoutContextProvider>
              </div>
            </I18nextProvider>
          </SiteProvider>
        </Suspense>
      </SentryErrorBoundary>
    </>
  );
};

export default App;
